import { Fragment, useEffect, useState } from "react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import usePriceSettingsMutations from "../../../data/usePriceSettingsMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import classNames from "../../../utils/classNames";
import { ICustomerGenericPriceSetting } from "../../../models/PriceSettings";

const options = [
  { id: "appliesToScrap", name: "Scrap" },
  { id: "appliesToSalvage", name: "Salvage" },
  { id: "appliesToSmart", name: "Smart" },
];

function getSelected(priceSetting: ICustomerGenericPriceSetting): string[] {
  return options.filter((o) => (priceSetting as any)[o.id]).map((o) => o.id);
}

export default function PricingTypeCell({
  priceSetting,
  info,
}: { priceSetting: ICustomerGenericPriceSetting, info: any }) {
  const [selected, setSelected] = useState<string[]>(getSelected(priceSetting));

  const { updateSpecific } = usePriceSettingsMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Specific Price Setting");

  useEffect(() => {
    setSelected(getSelected(priceSetting));
  }, [priceSetting.appliesToScrap, priceSetting.appliesToSalvage, priceSetting.appliesToSmart]);

  const handleChange = (newSelected: string[]) => {
    setSelected(newSelected);

    const updatedSetting = { ...priceSetting };
    options.forEach((o) => {
      (updatedSetting as any)[o.id] = newSelected.includes(o.id);
      info.table.options.meta?.updateData(info.row.index, o.id, newSelected.includes(o.id));
    });

    updateSpecific.mutate(updatedSetting, {
      onError: () => saveHandlers.onError(),
      onSuccess: () => saveHandlers.onSuccess(),
    });
  };

  const summary = options.filter((o) => selected.includes(o.id)).map((o) => o.name).join(", ") || "None";

  return (
    <span className="flex p-1">
      <Listbox value={selected} onChange={handleChange} multiple>
        {({ open }) => (
          <div className="w-full relative">
            <ListboxButton className="relative w-full text-xs text-left cursor-pointer border p-1 bg-white border-gray-300 rounded-md shadow-sm min-w-[100px]">
              <span className="block truncate pr-4">{summary}</span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-1 pointer-events-none">
                <ChevronUpDownIcon className="h-4 w-4 text-gray-700" aria-hidden="true" />
              </span>
            </ListboxButton>

            <Transition
              show={open}
              appear={true}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions className="absolute z-40 text-xs mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto">
                {options.map((o) => (
                  <ListboxOption
                    key={o.id}
                    className={({ active }: { active: boolean }) =>
                      classNames(
                        active ? "bg-primary-100 text-gray-900" : "text-gray-900",
                        "cursor-default select-none relative py-2 pl-8 pr-4"
                      )
                    }
                    value={o.id}
                  >
                    <span className={classNames(selected.includes(o.id) ? "font-semibold" : "font-normal", "block truncate")}>
                      {o.name}
                    </span>
                    {selected.includes(o.id) ? (
                      <span className="text-primary-600 absolute inset-y-0 left-0 flex items-center pl-1.5">
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </div>
        )}
      </Listbox>
    </span>
  );
}
